import { perStudentPriceInCents } from "@outschool/business-rules";
import { EnrollModalType } from "@outschool/data-schemas";
import { SettlementError } from "@outschool/errors";
import {
  EnrollmentInput,
  PricingOfferState,
} from "@outschool/gql-backend-generated";
import {
  ActivityEnrollQueryQuery,
  FullCurrentUserFragmentFragment,
  GetLearnerItems_EnrollmentFragment,
  ParentChildrenQuery,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { useProductAdded } from "@outschool/ui-analytics";
import { EsaSessionType, useSession } from "@outschool/ui-auth";
import { LoadingModal } from "@outschool/ui-legacy-component-library";
import { cleanErrorMessage } from "@outschool/ui-utils";
import React from "react";
import { useLocation } from "react-router";

import * as Env from "../../../shared/Env";
import * as Section from "../../../shared/Section";
import ErrorMessage from "../ErrorMessage";
import { LEARNER_NAME_MAX_LENGTH } from "../learner/LearnerNameWarning";
import ApplyPromoCode from "./enrollmentForm/ApplyPromoCode";
import EnrollmentModalActionButtons from "./enrollmentForm/EnrollmentModalActionButtons";
import { OngoingClassMeetingInputs } from "./enrollmentForm/OngoingClassMeetingInputs";
import OrderInputs from "./enrollmentForm/OrderInputs";
import { PrepayWeeklyPaymentsInputs } from "./enrollmentForm/PrepayWeeklyPaymentsInputs";
import PriceBreakdown from "./enrollmentForm/PriceBreakdown";
import PriceCreditsBreakdown from "./enrollmentForm/PriceCreditsBreakdown";
import RecurringSelection from "./enrollmentForm/RecurringSelection";
import { WarningMessages } from "./enrollmentForm/WarningMessages";
import { EsaEnrollmentIneligibleAlert } from "./EsaEnrollmentIneligibleAlert";
import { useOrderContext } from "./OrderContext";
import UpfrontOrWeeklyCreditsSelection from "./UpfrontOrWeeklyCreditsSelection";

export type EnrollmentFormNonNullable = NonNullable<
  ActivityEnrollQueryQuery["enrollmentForm"]
>;
export type EnrollmentFormProps = {
  activity: EnrollmentFormNonNullable["activity"];
  lastSubmittedEnrollments: GetLearnerItems_EnrollmentFragment[];
  section: EnrollmentFormNonNullable["section"] | null;
  leader: EnrollmentFormNonNullable["section"]["leader"] | null;
  enrollingParent?: ActivityEnrollQueryQuery["currentUser"];
  enrollingParentEnrollments: GetLearnerItems_EnrollmentFragment[];
  enrollmentsInOtherSections?: GetLearnerItems_EnrollmentFragment[];
  conflictingEnrollments?: GetLearnerItems_EnrollmentFragment[];
  filledSpaceCount: number;
  onClose(e: React.SyntheticEvent): void;
  error?: Error | string | null;
  disabled: boolean;
  enrollmentInProgress: boolean;
  enrollingOrgAdminUser?: FullCurrentUserFragmentFragment;
  requestAgeRangeException: (LearnerItemType: $TSFixMe) => void;
  onEnrollClick?(
    enrollmentInputs: EnrollmentInput[],
    donationAmountCents: number,
  ): void;
  disableOnSubmit?: boolean;
  setPurchaseOrderCoverage?: React.Dispatch<React.SetStateAction<Date | null>>;
  isUsingPurchaseOrder?: boolean;
  allowsFutureOngoingEnrollment?: boolean;
};

export default function EnrollmentForm({
  activity,
  section,
  leader,
  enrollingOrgAdminUser,
  enrollingParent,
  filledSpaceCount,
  onClose,
  error,
  disabled,
  enrollmentInProgress,
  onEnrollClick = () => {},
  requestAgeRangeException,
  disableOnSubmit = true,
  setPurchaseOrderCoverage,
  isUsingPurchaseOrder,
  allowsFutureOngoingEnrollment,
}: EnrollmentFormProps) {
  const { t } = useTranslation("client\\components\\enrolling\\EnrollmentForm");
  const { currentUser, esaSessionType } = useSession();
  const isClassWalletSession = esaSessionType === EsaSessionType.CW;
  const location = useLocation();
  const trackProductAdded = useProductAdded();
  const [highlightErrors, setHighlightErrors] = React.useState<boolean>(false);
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const {
    donationAmountCents,
    enrollmentInputs,
    buyingLearners,
    setLearnerItems,
    pricingBreakdown,
    learnerItems,
    hasEsaMembership,
  } = useOrderContext();
  const isSelfPaced = activity.isSelfPaced;
  const [localError, setLocalError] = React.useState<string>();
  const availableSpaces = isSelfPaced
    ? enrollmentInputs.length
    : !section
    ? 0
    : Section.availableSpaces(section, section?.filledSpaceCount);
  const numSpacesNeeded = enrollmentInputs.length;
  const { pricingOffer, pricingOfferState } = pricingBreakdown ?? {};
  const isPricingOfferValid =
    pricingOfferState === PricingOfferState.ValidFinancialAid ||
    pricingOfferState === PricingOfferState.ValidBuyerOrg;
  const hasBackgroundCheckExpired =
    !leader?.ownerOfSellerOrg && leader?.hasBackgroundCheckExpired;

  const disableForm =
    disabled ||
    enrollmentInProgress ||
    submitted ||
    (!!section && Section.isFull(section, filledSpaceCount)) ||
    !!hasBackgroundCheckExpired ||
    !activity.published_at;

  const validBuyingLearnerNames = buyingLearners.every(
    learner =>
      learner.name &&
      (learner.uid || learner.name.length <= LEARNER_NAME_MAX_LENGTH),
  );

  const formatError = useFormatSettlementError({
    availableSpaces,
    numSpacesNeeded,
  });

  /**
   * Handle enrollment form submission.
   * @param  {Event} e
   */
  const submit = (e: MouseEvent) => {
    e.preventDefault();

    if (disableForm || !validBuyingLearnerNames) {
      return;
    }

    if (!buyingLearners.length) {
      setLocalError("Please select the learner you'd like to enroll");
      return;
    }
    // Ensure that learners for which course is being purchased have necessary info.
    const validBuyingLearnersCount = buyingLearners.filter(
      learner =>
        learner.name &&
        learner.name.trim() !== "" &&
        (!!learner.age || !!learner.date_of_birth),
    ).length;
    const hasValidBuyingLearners =
      validBuyingLearnersCount > 0 &&
      validBuyingLearnersCount === buyingLearners.length;

    if (!hasValidBuyingLearners) {
      setHighlightErrors(true);
      return;
    }

    if (disableOnSubmit) {
      setSubmitted(true);
    }
    const enrollmentInputsAdjusted: EnrollmentInput[] = enrollmentInputs.map(
      enrollmentInput => {
        const price_cents =
          !!section && Section.isBuyoutSection(section)
            ? section.price_cents!
            : enrollmentInput.price_cents!;
        return {
          ...enrollmentInput,
          pricing_offer:
            isPricingOfferValid && !!pricingOffer ? pricingOffer.name : null,
          price_cents,
        };
      },
    );
    onEnrollClick(enrollmentInputsAdjusted, donationAmountCents);
  };

  React.useEffect(() => {
    const user = enrollingParent || enrollingOrgAdminUser;
    if (!user) {
      return;
    }
    trackProductAdded({
      activityListing: {
        activity: {
          ...activity,
          duration_weeks:
            activity.duration_weeks === null
              ? undefined
              : activity.duration_weeks,
        },
        leader: leader!,
      },
      user,
      pricingOffer: isPricingOfferValid ? pricingOffer?.name : undefined,
      enrollModalType: activity.isClub
        ? EnrollModalType.chat_groups
        : EnrollModalType.group_class,
    });
  }, [
    activity,
    enrollingOrgAdminUser,
    isPricingOfferValid,
    enrollingParent,
    leader,
    pricingOffer?.name,
    trackProductAdded,
  ]);

  const isWeeklyPaymentEligible = React.useMemo(() => {
    return (
      (activity.allows_recurring_payment &&
        !activity.isClub &&
        !!activity.duration_weeks &&
        activity.duration_weeks > 1) ||
      activity.is_ongoing_weekly
    );
  }, [
    activity.allows_recurring_payment,
    activity.is_ongoing_weekly,
    activity.isClub,
    activity.duration_weeks,
  ]);

  if (
    !activity ||
    !activity.details ||
    (!section && !isSelfPaced) ||
    !currentUser
  ) {
    return <LoadingModal />;
  }
  const ignoreStrictAgeRange =
    !!section && Section.ignoresStrictAgeRange(section);
  const priceCents = perStudentPriceInCents(
    activity,
    section,
    undefined,
    currentUser.hasSubscription,
  );
  const isBuyoutSection = !!section && Section.isBuyoutSection(section);

  const isPrepayUser =
    isUsingPurchaseOrder || !!enrollingOrgAdminUser || isClassWalletSession;

  const selectedLearners = learnerItems
    .filter(item => item.checked)
    .map(item => item.key);

  const parentByEmail = enrollingParent as ParentChildrenQuery["parentByEmail"];
  const isPrepayEnrollment = !!parentByEmail?.enrollments?.find(
    enrollment =>
      enrollment?.section_uid === section?.uid && enrollment?.isPrepay,
  );

  return (
    <form method="POST" onSubmit={e => e.preventDefault()}>
      {!currentUser.canPayWithSubscriptionCredits && (
        <WarningMessages
          enrollingParent={enrollingParent}
          pricingOfferState={pricingOfferState}
          pricingOffer={pricingOffer}
        />
      )}
      {(error || localError) && (
        <ErrorMessage
          value={formatError(error) || localError}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
      )}
      {(location.state as any)?.redirectedFromClassWalletCheckout && (
        <ErrorMessage value="You are no longer in a ClassWallet session. If you would like to checkout with ClassWallet funds, please log back in through ClassWallet. Otherwise, just continue the checkout process." />
      )}
      {hasBackgroundCheckExpired && (
        <ErrorMessage
          value={t`This educator is not accepting new enrollments at this time.  Please message the educator with your request or check back soon. Thank you for your continued interest in our programs.`}
        />
      )}
      <OrderInputs
        disabled={disableForm}
        activity={activity}
        ignoreStrictAgeRange={ignoreStrictAgeRange}
        learnerItems={learnerItems}
        onLearnerItemsUpdate={setLearnerItems}
        highlightErrors={highlightErrors}
        buyingLearners={buyingLearners}
        requestAgeRangeException={requestAgeRangeException}
        onlyAllowSingleLearner={
          isSelfPaced || (isPrepayUser && isPrepayEnrollment)
        }
        isPrepayEnrollment={isPrepayEnrollment}
        usingEsaFunds={hasEsaMembership}
      />
      {!!pricingBreakdown && (
        <EsaEnrollmentIneligibleAlert pricingBreakdown={pricingBreakdown} />
      )}

      {!!activity.is_ongoing_weekly &&
        enrollingOrgAdminUser &&
        !isPrepayUser && (
          <OngoingClassMeetingInputs
            shouldShowStartMeeting={false}
            shouldShowEndMeeting
          />
        )}

      {isWeeklyPaymentEligible && isPrepayUser && !!section && (
        <PrepayWeeklyPaymentsInputs
          setPurchaseOrderCoverage={setPurchaseOrderCoverage}
          section={section}
          selectedLearners={selectedLearners}
          isOngoing={activity.is_ongoing_weekly}
        />
      )}

      {enrollingParent &&
        !isPrepayUser &&
        !currentUser.canPayWithSubscriptionCredits && (
          <RecurringSelection activity={activity} section={section} />
        )}
      {enrollingParent && currentUser.canPayWithSubscriptionCredits && (
        <UpfrontOrWeeklyCreditsSelection
          activity={activity}
          section={section}
        />
      )}

      {priceCents > 0 && <ApplyPromoCode />}
      {!currentUser.canPayWithSubscriptionCredits && (
        <PriceBreakdown
          activity={activity}
          section={section}
          enrollingOrgAdminUser={enrollingOrgAdminUser}
          priceCents={priceCents}
          isBuyoutSection={isBuyoutSection}
          enrollmentInputs={enrollmentInputs}
          donationInput={{
            amountCents: donationAmountCents,
          }}
        />
      )}

      {currentUser.canPayWithSubscriptionCredits && (
        <PriceCreditsBreakdown activity={activity} />
      )}

      <EnrollmentModalActionButtons
        cancelLabel={
          activity.isSelfPaced || allowsFutureOngoingEnrollment
            ? t("Back")
            : t("Cancel")
        }
        onCancel={onClose}
        disabled={disableForm || !validBuyingLearnerNames}
        primaryLabel={
          currentUser?.canPayWithSubscriptionCredits
            ? pricingBreakdown?.subscriptionOutstandingBalanceCredits
              ? t("Pay Outstanding Balance")
              : t("Enroll")
            : t("Next")
        }
        immediatePrimaryAction={submit}
        trackingName="continue_to_payment"
        trackingSharedProperties={{
          activityUid: activity.uid,
          isUsingPurchaseOrder,
          isBuyoutSection,
          enrollmentFormType: "default",
        }}
      />
    </form>
  );
}

function useFormatSettlementError(data: Record<string, number>) {
  const { t } = useTranslation("client\\components\\enrolling\\EnrollmentForm");
  return React.useCallback(
    (error: Error | string | null | undefined) => {
      if (!(error instanceof Error)) {
        return error;
      }

      switch (cleanErrorMessage(error.message)) {
        case SettlementError.SectionSoldOut:
          return "This section is sold out.";
        case SettlementError.SectionAlmostSoldOut:
          const { availableSpaces, numSpacesNeeded } = data as {
            availableSpaces: number;
            numSpacesNeeded: number;
          };
          return t(
            "This section is almost sold out: only {{availableSpaces}} spot available of the requested {{numSpacesNeeded}}.",
            {
              availableSpaces,
              numSpacesNeeded,
              count: availableSpaces,
              defaultValue_plural:
                "This section is almost sold out: only {{availableSpaces}} spots available of the requested {{numSpacesNeeded}}.",
            },
          );
        case SettlementError.SectionCanceled:
          return "This section has been canceled.";
        case SettlementError.SectionUnavailable:
          return "This section is not open for enrollment.";
        default:
          return error.message;
      }
    },
    [t, data],
  );
}
